/* eslint-disable max-len */
// src/components/ImageSelectionDrawer/ImageSelectionDrawer.tsx
import React, { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Drawer, Input, Upload, message, Collapse, Spin,
} from 'antd';
import { DownloadOutlined, LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import API, {
  CatalogCandidate, CatalogFragment, SimilarCatalogCandidate, UnsplashSearchItem,
} from '../../services/ApiService';
import { ScreenshotUpload } from '../ScreenshotUpload/ScreenshotUpload';
import { buildPhotoFormData, validatePhotoFile } from '../../utils/photoUpload';
import { rewriteForImageSearch } from '../../utils/imageSearchQuery';
import { withUnsplashUtm } from '../../utils/unsplashAttribution';
import { unsplashErrorKey } from '../../utils/unsplashErrors';
import { isCatalogTerm } from '../../utils/catalog';
import { toSearchWords } from '../../utils/search';
import classes from './ImageSelectionDrawer.module.less';

const { Panel } = Collapse;

// Tyle kafelków wypełnia szufladę bez przewijania
const PER_PAGE = 14;

// Pole szuka w katalogu w trakcie pisania, po takiej pauzie (jak wyszukiwarka na ekranie z listami)
const FIELD_SEARCH_DELAY_MS = 300;
// Po kawałkach słów szukamy od tylu liter lub cyfr: w polu od 2, bo pojedyncza pasowałaby do prawie
// każdego zwrotu, a przy otwarciu od 3 — tam szuflada podpowiada sama, a „on" czy „in" złapałyby
// wszystkie zwroty z tym słowem, zamiast od razu wczytać zdjęcia z banku
const FIELD_FRAGMENT_MIN_LENGTH = 2;
const WORD_FRAGMENT_MIN_LENGTH = 3;

// Przy szukaniu po kawałkach słów liczą się tylko litery i cyfry
const countSearchChars = (text: string): number => toSearchWords(text).join('').length;

// Tekst z pola, którego szuflada szuka, albo '' — krótszy tekst działa jak puste pole i liczy się zwrot słowa
const toFieldTerm = (value: string): string => (
  countSearchChars(value) >= FIELD_FRAGMENT_MIN_LENGTH ? value.trim() : ''
);

// Komplet danych o wybranym obrazku. Dla Unsplasha atrybucja jest wymagana przez wytyczne API,
// a `normalizedUrl` jest kluczem dopasowania — `urls.small` niesie jednorazowy `ixid`.
// Od R9 cały ten komplet przychodzi gotowy z backendu; front go kopiuje, nie składa.
// Katalog oddaje `source` jako dowolny tekst (dziś zawsze 'upload'), stąd `string`.
export interface SelectedImage {
  url: string;
  source: string;
  imageAssetId?: number;
  photoId?: string;
  authorName?: string;
  authorUrl?: string;
  sourceUrl?: string;
  normalizedUrl?: string | null;
}

interface ImageSelectionDrawerProps {
  visible: boolean;
  wordsListId?: string;
  selectedWordId: string | null;
  onClose: () => void;
  // `fromCatalog` — obrazek wybrany z katalogu dla tego właśnie zwrotu, więc ta para już w nim jest
  onImageSelected: (image: SelectedImage, fromCatalog?: boolean) => void;
  initialSearchTerm?: string;
}

// Obrazki z katalogu dla jednego hasła: dokładne trafienia i podobne zwroty, razem z hasłem, którego dotyczą
interface CatalogResults {
  term: string;
  candidates: CatalogCandidate[];
  similar: SimilarCatalogCandidate[];
}

const NO_CATALOG_RESULTS: CatalogResults = { term: '', candidates: [], similar: [] };

export const ImageSelectionDrawer: FC<ImageSelectionDrawerProps> = ({
  visible,
  wordsListId,
  selectedWordId,
  onClose,
  onImageSelected,
  initialSearchTerm = '',
}) => {
  const { t } = useTranslation();
  const [photoResults, setPhotoResults] = useState<UnsplashSearchItem[]>([]);
  // Hasło, dla którego są pokazane zdjęcia z banku; '' — jeszcze żadnych
  const [photosTerm, setPhotosTerm] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [catalogResults, setCatalogResults] = useState<CatalogResults>(NO_CATALOG_RESULTS);
  const [isCatalogLoading, setIsCatalogLoading] = useState(false);
  // Numer ostatniego zapytania do katalogu — odpowiedź na starsze niczego nie ustawia
  const catalogRequestRef = useRef(0);
  // Szukanie w katalogu, które czeka na pauzę w pisaniu
  const fieldSearchTimerRef = useRef<NodeJS.Timeout>();

  // Szuflada szuka tekstu z pola, a przy pustym polu zwrotu słowa — dla niego są obrazy z katalogu
  // na górze i kafelek „Więcej zdjęć"
  const fieldTerm = toFieldTerm(inputValue);
  const activeTerm = fieldTerm || initialSearchTerm;
  // Bank zdjęć szuka tylko wtedy, gdy zdjęć dla tego tekstu jeszcze nie ma — limit zapytań jest wspólny
  const canSearchPhotos = Boolean(activeTerm) && photosTerm !== activeTerm && !isSearching;

  // `query` leci do banku zdjęć, `label` to hasło, dla którego zdjęcia są potem pokazane — zwrot słowa
  // albo tekst z pola, nie opis sceny. Zwraca liczbę wyników albo `null`, gdy zapytanie padło — `null`
  // wstrzymuje próbę z frazą zapasową, żeby przy wyczerpanej puli nie strzelać drugi raz i nie dublować komunikatu.
  const fetchPhotosApi = (query: string, label: string = query): Promise<number | null> => {
    setIsSearching(true);

    return API.images.searchUnsplash({
      query,
      perPage: PER_PAGE,
      orientation: 'landscape',
    }).then((response) => {
      const results = response.data.results || [];

      setPhotoResults(results);
      setPhotosTerm(label);

      return results.length;
    }).catch((error) => {
      message.error(t(unsplashErrorKey(error)));

      return null;
    }).finally(() => {
      setIsSearching(false);
    });
  };

  // Hasło idzie surowe, bo normalizuje backend. Pusty albo za długi zwrot nie ma w katalogu czego szukać.
  // `fragment` — szukanie także po kawałkach słów: tekst z pola, a zwrot słowa, gdy nic innego nie pasuje.
  const fetchCatalog = (term: string, fragment?: CatalogFragment): Promise<CatalogResults> => {
    if (!isCatalogTerm(term)) {
      return Promise.resolve({ ...NO_CATALOG_RESULTS, term });
    }

    return API.catalog.search(term, fragment)
      .then(({ data }) => ({ term, candidates: data.candidates, similar: data.similar || [] }))
      // Katalog jest podpowiedzią — gdy nie odpowie, szuflada działa tak jak przed nim
      .catch(() => ({ ...NO_CATALOG_RESULTS, term }));
  };

  // Propozycje dla zwrotu słowa — przy otwarciu i po wyczyszczeniu pola. Najpierw zwroty takie same
  // i podobne; gdy katalog nie ma żadnych, także zwroty z tymi słowami albo ich początkami („mia" → „miau",
  // ale „car" już nie → „scary"). Katalog jest pierwszym wyborem, bank zdjęć zapasem — a „get on" z własnym
  // wpisem nie ściąga wszystkich dłuższych „get on…".
  const fetchWordProposals = (): Promise<CatalogResults> => fetchCatalog(initialSearchTerm)
    .then((results) => (
      results.candidates.length || results.similar.length
        || countSearchChars(initialSearchTerm) < WORD_FRAGMENT_MIN_LENGTH
        ? results
        : fetchCatalog(initialSearchTerm, 'start')
    ));

  // Obrazki z katalogu zawsze dotyczą ostatnio szukanego hasła. `null` — w międzyczasie poszło nowsze
  // zapytanie albo szuflada się zamknęła, więc ta odpowiedź niczego nie ustawia.
  const searchCatalog = (request: Promise<CatalogResults>): Promise<CatalogResults | null> => {
    catalogRequestRef.current += 1;
    const requestId = catalogRequestRef.current;

    setIsCatalogLoading(true);

    return request.then((results) => {
      if (requestId !== catalogRequestRef.current) {
        return null;
      }

      setCatalogResults(results);
      setIsCatalogLoading(false);

      return results;
    });
  };

  const cancelFieldSearch = () => {
    if (fieldSearchTimerRef.current) {
      clearTimeout(fieldSearchTimerRef.current);
    }
  };

  // Pisanie w polu szuka w katalogu po krótkiej pauzie, także po kawałkach słów — „inter" znajdzie
  // „be interested in (music)". Gdy tekst przestaje się liczyć (puste pole, jedna litera), na górę od razu
  // wracają propozycje dla słowa. Bank zdjęć czeka na Enter albo kafelek, a jego zdjęcia przy pisaniu zostają.
  const handleInputChange = (value: string) => {
    const nextFieldTerm = toFieldTerm(value);

    setInputValue(value);

    // Np. pierwsza litera albo spacja na końcu — dla katalogu nic się nie zmienia
    if (nextFieldTerm === fieldTerm) {
      return;
    }

    cancelFieldSearch();

    if (nextFieldTerm) {
      fieldSearchTimerRef.current = setTimeout(
        () => searchCatalog(fetchCatalog(nextFieldTerm, true)),
        FIELD_SEARCH_DELAY_MS,
      );
    } else {
      searchCatalog(fetchWordProposals());
    }
  };

  // Hasło przepisane na opis sceny. Gdy przepisana fraza nic nie da, druga próba idzie z oryginałem.
  // Fraza wpisana ręcznie (searchPhotos) idzie do banku zdjęć bez zmian.
  const searchPhotoBank = (term: string) => {
    const query = rewriteForImageSearch(term);

    fetchPhotosApi(query, term).then((found) => {
      if (found === 0 && query !== term) {
        fetchPhotosApi(term);
      }
    });
  };

  // Enter i kafelek „Więcej zdjęć" robią to samo: zdjęcia z banku dla tekstu z pola, a przy pustym polu
  // dla zwrotu słowa. Gdy zdjęcia dla tego tekstu już są albo właśnie się ładują, nic nie wysyłają.
  const searchPhotos = () => {
    if (!canSearchPhotos) {
      return;
    }

    if (fieldTerm) {
      fetchPhotosApi(fieldTerm);
    } else {
      searchPhotoBank(initialSearchTerm);
    }
  };

  const handleClose = () => {
    // Odpowiedź z katalogu, która przyjdzie już po zamknięciu, nie może niczego ustawić
    catalogRequestRef.current += 1;
    cancelFieldSearch();
    setPhotoResults([]);
    setInputValue('');
    setPhotosTerm('');
    setCatalogResults(NO_CATALOG_RESULTS);
    setIsCatalogLoading(false);
    onClose();
  };

  // Podstawienie z katalogu działa jak każdy inny wybór: `imageMeta` idzie do słowa bez zmian, a zwrot
  // podobnego obrazka (`conceptKey`) zostaje w szufladzie. `isWordPair` — obrazek jest w katalogu
  // dokładnie przy zwrocie tego słowa.
  const handleCatalogSelect = (candidate: CatalogCandidate, isWordPair: boolean) => {
    onImageSelected({
      url: candidate.url,
      imageAssetId: candidate.assetId,
      ...candidate.imageMeta,
    }, isWordPair);
    handleClose();
  };

  // Bez udanego `select` nie ma ani `assetId`, ani zgłoszenia użycia zdjęcia do Unsplasha —
  // dlatego przy błędzie obrazek się NIE ustawia, a szuflada zostaje otwarta na kolejną próbę.
  const handlePhotoSelect = async (photo: UnsplashSearchItem) => {
    if (isSelecting) {
      return;
    }

    setIsSelecting(true);

    try {
      const { data } = await API.images.selectUnsplash(photo.photoId);

      onImageSelected({
        url: data.url,
        imageAssetId: data.assetId,
        ...data.imageMeta,
      });
      handleClose();
    } catch (error) {
      message.error(t(unsplashErrorKey(error)));
    } finally {
      setIsSelecting(false);
    }
  };

  // Przy otwarciu najpierw katalog. Bank zdjęć szuka sam tylko wtedy, gdy katalog nie ma nic — ani
  // dokładnego trafienia, ani podobnego zwrotu, ani zwrotu pasującego po kawałkach słów; inaczej na kafelek
  // „Więcej zdjęć" — katalog jest pierwszym wyborem, a limit zapytań do banku wspólny.
  React.useEffect(() => {
    if (!visible || !initialSearchTerm) {
      return undefined;
    }

    searchCatalog(fetchWordProposals()).then((results) => {
      if (results && !results.candidates.length && !results.similar.length) {
        searchPhotoBank(initialSearchTerm);
      }
    });

    // Odpowiedź dla słowa, którego szuflada już nie pokazuje, nie może niczego ustawić
    return () => {
      catalogRequestRef.current += 1;
    };
  }, [visible, initialSearchTerm]);

  // Szukanie zaplanowane tuż przed wyjściem z ekranu nie może już niczego ustawić
  React.useEffect(() => () => {
    if (fieldSearchTimerRef.current) {
      clearTimeout(fieldSearchTimerRef.current);
    }
  }, []);

  // Dokładne trafienie dla hasła słowa to para, która już jest w katalogu. Po wyszukaniu innej frazy
  // obrazek z katalogu tworzy parę z tamtą frazą, nie ze słowem.
  const isWordPhrase = catalogResults.term === initialSearchTerm;

  return (
    <Drawer
      title={t('words-list:images.select')}
      width="360px"
      bodyStyle={{ backgroundColor: '#002525' }}
      headerStyle={{ backgroundColor: 'lightgray' }}
      placement="right"
      onClose={handleClose}
      visible={visible}
    >
      {/* Komponent obsługujący paste i drag&drop */}
      <ScreenshotUpload
        wordsListId={wordsListId}
        // Jak przy każdym innym wyborze szuflada sprząta po sobie — tekst z pola nie wróci przy następnym słowie
        onImageUploaded={(url, assetId) => {
          onImageSelected({ url, source: 'upload', imageAssetId: assetId });
          handleClose();
        }}
        isActive={visible}
      />

      {/* Panel z instrukcjami */}
      <Collapse
        ghost
        className={classes.instructionsCollapse}
        expandIcon={({ isActive }) => (
          <QuestionCircleOutlined
            rotate={isActive ? 540 : 0}
            className={classes.expandIcon}
          />
        )}
      >
        <Panel
          header={(
            <span className={classes.instructionsHeader}>
              {t('words-list:screenshot.title')}
            </span>
          )}
          key="1"
          className={classes.instructionsPanel}
        >
          <div className={classes.instructionsContent}>
            <div className={classes.instructionsTitle}>
              <strong>{t('words-list:screenshot.quickAdd')}</strong>{t('words-list:screenshot.quickAddRest')}
              {' '}{t('words-list:screenshot.noNeedToExpand')}<br />
              {t('words-list:screenshot.dragStep')}<br />
              {t('words-list:screenshot.pasteStep')}<br />
              {t('words-list:screenshot.instant')}
            </div>
            ---------------------------------
            <div className={classes.instructionsStep}>
              <strong>{t('words-list:screenshot.shortcuts')}</strong>
            </div>
            <div className={classes.instructionsStep}>
              <strong>Windows:</strong>
              {t('words-list:screenshot.windows')}
            </div>
            <div className={classes.instructionsStep}>
              <strong>macOS:</strong>
              {t('words-list:screenshot.macos')}
            </div>
          </div>
        </Panel>
      </Collapse>

      {/* Upload z dysku */}
      <Upload
        name="file"
        accept="image/png, image/jpeg"
        beforeUpload={(file) => {
          const validationError = validatePhotoFile(file);
          if (validationError) {
            message.error(validationError);

            return Upload.LIST_IGNORE;
          }

          return true;
        }}
        customRequest={async ({ file, onSuccess: onUploadSuccess, onError: onUploadError }) => {
          try {
            const data = buildPhotoFormData(file as File, wordsListId);

            const response = await API.photos.add(data);
            const photo = response.data;

            if (selectedWordId) {
              onImageSelected({ url: photo.url, source: 'upload', imageAssetId: photo.assetId });
            }
            onUploadSuccess?.(photo);
            handleClose();
          } catch (uploadError) {
            onUploadError?.(uploadError as any);
            message.error(t('words-list:photo.messages.error'));
          }
        }}
        showUploadList={false}
      >
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          size="large"
          className={classes.loadButton}
        >
          {t('words-list:images.loadFromComputer')}
        </Button>
      </Upload>

      {/* Wyszukiwanie od otwarcia, także gdy bank zdjęć nie odpowiada: w trakcie pisania katalog, po Enterze
          także bank. Puste pole pokazuje zwrot słowa. `suffix` zawsze — antd przy jego dodaniu albo usunięciu
          tworzy pole od nowa i gubi kursor. */}
      <Input
        type="text"
        placeholder={initialSearchTerm}
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onPressEnter={searchPhotos}
        suffix={isCatalogLoading ? <LoadingOutlined /> : <span />}
        className={classes.inputDrawer}
      />

      {/* Galeria zdjęć z Unsplash. Wybór to teraz strzał do naszego API, więc kręciołek
          przykrywa i wyszukiwanie, i czekanie na `select`. Szukanie w katalogu kręci się tylko w polu —
          kafelki zmieniają się dopiero z nowymi wynikami, bez migania. */}
      <Spin spinning={isSearching || isSelecting}>
        <div className={classes.picturesOptionsContainer}>
          {/* Najpierw obrazki z katalogu dla szukanego hasła, bez wyróżnienia — dla nauczyciela to po prostu pierwsze propozycje */}
          {catalogResults.candidates.map((candidate) => (
            <div
              key={`catalog-${candidate.assetId}`}
              tabIndex={0}
              role="button"
              aria-label={t('words-list:images.selectPhoto')}
              onClick={() => handleCatalogSelect(candidate, isWordPhrase)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleCatalogSelect(candidate, isWordPhrase);
                }
              }}
            >
              <img
                src={candidate.url}
                alt={catalogResults.term}
                className={classes.pictureOption}
              />
            </div>
          ))}

          {/* Potem obrazki z podobnych zwrotów, z podpisem — „turn sb on" to nie „turn on", a bez podpisu
              nauczyciel by tego nie wiedział. Cały zwrot w podpowiedzi, gdy nie mieści się na pasku. */}
          {catalogResults.similar.map((candidate) => (
            <div
              key={`similar-${candidate.assetId}`}
              className={classes.similarOption}
              title={candidate.conceptKey}
              tabIndex={0}
              role="button"
              aria-label={t('words-list:images.selectPhoto')}
              onClick={() => handleCatalogSelect(candidate, false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleCatalogSelect(candidate, false);
                }
              }}
            >
              <img
                src={candidate.url}
                alt={candidate.conceptKey}
                className={classes.pictureOption}
              />
              <span className={classes.similarCaption}>{candidate.conceptKey}</span>
            </div>
          ))}

          {/* Pierwszy wolny kafelek: bank zdjęć na życzenie, to samo co Enter. Stoi, dopóki pod nim nie ma zdjęć
              dla tekstu z pola, a przy pustym polu — dla zwrotu słowa. Przy otwarciu czeka na katalog, bo gdy
              ten nie ma nic, bank szuka sam. */}
          {canSearchPhotos && Boolean(catalogResults.term) && (
            <div
              className={classes.moreTile}
              tabIndex={0}
              role="button"
              onClick={searchPhotos}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  searchPhotos();
                }
              }}
            >
              {t('words-list:images.morePhotos')}
            </div>
          )}

          {/* Zdjęcia z banku pod obrazami z katalogu — wszystkie pobrane, przy dużym katalogu okno się przewija */}
          {photoResults.map((photo) => (
            <div
              className={classes.picturesOption}
              key={photo.photoId}
              tabIndex={0}
              role="button"
              aria-label={t('words-list:images.selectPhoto')}
              onClick={() => handlePhotoSelect(photo)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handlePhotoSelect(photo);
                }
              }}
            >
              <img
                src={photo.urls.small}
                alt={photo.description || 'Photo'}
                className={classes.pictureOption}
              />
            </div>
          ))}
        </div>
      </Spin>

      {/* Wzmianka o Unsplashu na ekranie wyszukiwania */}
      <a
        className={classes.unsplashCredit}
        href={withUnsplashUtm('https://unsplash.com/')}
        target="_blank"
        rel="noreferrer"
      >
        Powered by Unsplash.com
      </a>
    </Drawer>
  );
};
